/**
 * Browser-compatible PostgreSQL interface
 * 
 * This is a mock implementation for the browser environment that will delegate
 * actual operations to the backend API. PostgreSQL cannot run directly in a browser.
 */
import { auth } from '../firebase/core';
import logger from '../../utils/logger';

export class PostgresDB {
  private apiBaseUrl: string;
  private isConnected: boolean = false;

  constructor() {
    this.apiBaseUrl = process.env.REACT_APP_API_URL || '/api';
    logger.info('[PostgresDB] API Base URL:', this.apiBaseUrl);
  }

  /**
   * Initialize the database connection
   */
  public async initialize(): Promise<boolean> {
    try {
      // In browser, we just check API connectivity
      const healthUrl = `${this.apiBaseUrl}/health`;
      logger.info('[PostgresDB] Checking health at:', healthUrl);
      const response = await fetch(healthUrl);
      this.isConnected = response.ok;
      logger.info('[PostgresDB] Health check response:', response.status, response.ok);
      return this.isConnected;
    } catch (error) {
      logger.error('Error initializing PostgreSQL connection', error);
      this.isConnected = false;
      return false;
    }
  }

  /**
   * Check if the database is healthy and accessible
   */
  public async isHealthy(): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/health`);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Execute a search query through the API
   */
  private async executeSearch(path: string, query: string, limit: number = 10, offset: number = 0): Promise<any[]> {
    try {
      // Get the current user's auth token
      const authToken = await this.getAuthToken();
      
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
        logger.info('[PostgresDB] Auth token present (first 20 chars):', authToken.substring(0, 20) + '...');
      } else {
        logger.warn('[PostgresDB] No auth token available');
      }
      
      const searchUrl = `${this.apiBaseUrl}/api/search/${path}?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`;
      logger.info('[PostgresDB] Executing search:', searchUrl);
      
      const response = await fetch(searchUrl, {
        method: 'GET',
        headers
      });
      
      logger.info('[PostgresDB] Search response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`[PostgresDB] API search failed:`, {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        // Return empty array on failure rather than throwing
        return [];
      }
      
      const data = await response.json();
      logger.info('[PostgresDB] Search response data:', {
        total: data.total || 0,
        resultsCount: data.results?.length || 0,
        hasResults: !!data.results
      });
      
      return data.results || [];
    } catch (error) {
      logger.error(`[PostgresDB] Error executing search query for ${path}:`, error);
      return [];
    }
  }
  
  /**
   * Get the authentication token from Firebase Auth
   */
  private async getAuthToken(): Promise<string | null> {
    try {
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        return null;
      }
      
      // Get the ID token
      const token = await currentUser.getIdToken();
      return token;
    } catch (error) {
      logger.error('Error getting auth token', error);
      return null;
    }
  }

  /**
   * Search for users
   */
  public async searchUsers(query: string, limit: number = 10, offset: number = 0): Promise<any[]> {
    return this.executeSearch('users', query, limit, offset);
  }

  /**
   * Search for techniques
   */
  public async searchTechniques(query: string, limit: number = 10, offset: number = 0): Promise<any[]> {
    return this.executeSearch('techniques', query, limit, offset);
  }

  /**
   * Search for trainings
   */
  public async searchTrainings(query: string, limit: number = 10, offset: number = 0): Promise<any[]> {
    return this.executeSearch('trainings', query, limit, offset);
  }

  /**
   * Search for posts
   */
  public async searchPosts(query: string, limit: number = 10, offset: number = 0): Promise<any[]> {
    return this.executeSearch('posts', query, limit, offset);
  }

  /**
   * Search for workout routines
   */
  public async searchWorkoutRoutines(query: string, limit: number = 10, offset: number = 0): Promise<any[]> {
    return this.executeSearch('workout-routines', query, limit, offset);
  }
}

const postgresDBInstance = new PostgresDB();

export default postgresDBInstance;